<template>
  <div 
    ref="triggerRef"
    class="tooltip-trigger"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @focus="showTooltip"
    @blur="hideTooltip"
  >
    <slot />
    <Teleport to="body">
      <div
        v-if="isVisible"
        ref="tooltipRef"
        :class="[...tooltipClass, { 'tooltip--visible': isVisible }]"
        :style="tooltipStyle"
        role="tooltip"
        :aria-describedby="tooltipId"
      >
        <div class="tooltip-content">
          <slot name="content">{{ content }}</slot>
        </div>
        <div class="tooltip-arrow" :class="`tooltip-arrow--${placement}`"></div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';

export interface TooltipProps {
  content?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  disabled?: boolean;
  maxWidth?: string;
}

const props = withDefaults(defineProps<TooltipProps>(), {
  placement: 'top',
  delay: 500,
  disabled: false,
  maxWidth: '200px',
});

const triggerRef = ref<HTMLElement>();
const tooltipRef = ref<HTMLElement>();
const isVisible = ref(false);
const showTimer = ref<number | undefined>();
const hideTimer = ref<number | undefined>();
const tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`;

const tooltipClass = computed(() => [
  'tooltip',
  `tooltip--${props.placement}`,
]);

const tooltipStyle = ref({
  top: '0px',
  left: '0px',
  maxWidth: props.maxWidth,
});

const updatePosition = async () => {
  if (!triggerRef.value || !tooltipRef.value) return;
  
  await nextTick();
  
  const trigger = triggerRef.value.getBoundingClientRect();
  const tooltip = tooltipRef.value.getBoundingClientRect();
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  };
  
  let top = 0;
  let left = 0;
  
  switch (props.placement) {
    case 'top':
      top = trigger.top - tooltip.height - 8;
      left = trigger.left + (trigger.width - tooltip.width) / 2;
      break;
    case 'bottom':
      top = trigger.bottom + 8;
      left = trigger.left + (trigger.width - tooltip.width) / 2;
      break;
    case 'left':
      top = trigger.top + (trigger.height - tooltip.height) / 2;
      left = trigger.left - tooltip.width - 8;
      break;
    case 'right':
      top = trigger.top + (trigger.height - tooltip.height) / 2;
      left = trigger.right + 8;
      break;
  }
  
  // Keep tooltip within viewport
  if (left < 8) left = 8;
  if (left + tooltip.width > viewport.width - 8) {
    left = viewport.width - tooltip.width - 8;
  }
  if (top < 8) top = 8;
  if (top + tooltip.height > viewport.height - 8) {
    top = viewport.height - tooltip.height - 8;
  }
  
  tooltipStyle.value = {
    top: `${top + window.scrollY}px`,
    left: `${left + window.scrollX}px`,
    maxWidth: props.maxWidth,
  };
};

const showTooltip = () => {
  if (props.disabled) return;
  
  if (hideTimer.value) {
    window.clearTimeout(hideTimer.value);
    hideTimer.value = undefined;
  }
  
  showTimer.value = window.setTimeout(() => {
    isVisible.value = true;
    nextTick(() => updatePosition());
  }, props.delay);
};

const hideTooltip = () => {
  if (showTimer.value) {
    window.clearTimeout(showTimer.value);
    showTimer.value = undefined;
  }
  
  hideTimer.value = window.setTimeout(() => {
    isVisible.value = false;
  }, 100);
};

const handleResize = () => {
  if (isVisible.value) {
    updatePosition();
  }
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleResize);
  
  if (showTimer.value) window.clearTimeout(showTimer.value);
  if (hideTimer.value) window.clearTimeout(hideTimer.value);
});
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.tooltip-trigger {
  display: inline-block;
}

.tooltip {
  position: absolute;
  z-index: 9999;
  background-color: var(--vscode-editor-hoverHighlightBackground);
  border: 1px solid var(--vscode-contrastBorder);
  border-radius: 3px;
  box-shadow: 0 2px 8px var(--vscode-widget-shadow);
  pointer-events: none;
  max-width: 250px;
  opacity: 0;
  transform: scale(0.95);
  transition: opacity 0.15s ease, transform 0.15s ease;
  
  &.tooltip--visible {
    opacity: 1;
    transform: scale(1);
  }
  
  &--top .tooltip-content {
    margin-bottom: 6px;
  }
  
  &--bottom .tooltip-content {
    margin-top: 6px;
  }
  
  &--left .tooltip-content {
    margin-right: 6px;
  }
  
  &--right .tooltip-content {
    margin-left: 6px;
  }
}

.tooltip-content {
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--vscode-editor-foreground);
  font-size: 12px;
  font-family: var(--vscode-font-family);
  line-height: 1.4;
  word-wrap: break-word;
}

.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border: 6px solid transparent;
  
  &--top {
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-top-color: var(--vscode-contrastBorder);
    border-bottom: none;
    
    &::after {
      content: '';
      position: absolute;
      top: -7px;
      left: -5px;
      border: 5px solid transparent;
      border-top-color: var(--vscode-editor-hoverHighlightBackground);
      border-bottom: none;
    }
  }
  
  &--bottom {
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-bottom-color: var(--vscode-contrastBorder);
    border-top: none;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -7px;
      left: -5px;
      border: 5px solid transparent;
      border-bottom-color: var(--vscode-editor-hoverHighlightBackground);
      border-top: none;
    }
  }
  
  &--left {
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-left-color: var(--vscode-contrastBorder);
    border-right: none;
    
    &::after {
      content: '';
      position: absolute;
      left: -7px;
      top: -5px;
      border: 5px solid transparent;
      border-left-color: var(--vscode-editor-hoverHighlightBackground);
      border-right: none;
    }
  }
  
  &--right {
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-right-color: var(--vscode-contrastBorder);
    border-left: none;
    
    &::after {
      content: '';
      position: absolute;
      right: -7px;
      top: -5px;
      border: 5px solid transparent;
      border-right-color: var(--vscode-editor-hoverHighlightBackground);
      border-left: none;
    }
  }
}
</style>
